/**
 * pdfGenerator — Génération PDF des analyses IA via window.print().
 *
 * On ouvre une fenêtre stylée print-friendly et on déclenche le dialogue
 * d'impression du navigateur — l'utilisateur enregistre en PDF (option
 * native sur tous les navigateurs modernes). Plus simple et plus fiable
 * que les libs qui rendent du PDF côté JS.
 */

import DOMPurify from 'dompurify';

export interface GeneratePdfOptions {
  title: string;
  subtitle?: string;
  clientName: string;
  date: string;
  htmlContent: string;
  filename: string;
}

/** CSS print-friendly inline pour la fenêtre d'export. */
const PRINT_CSS = `
  @page {
    size: A4;
    margin: 18mm 16mm 22mm 16mm;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', 'Helvetica', 'Arial', sans-serif;
    font-size: 10.5pt;
    line-height: 1.55;
    color: #333333;
    background: #fafafa;
  }

  /* ─── Page de garde ─── */
  .cover {
    page-break-after: always;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 8mm;
  }
  .cover-decor {
    width: 60px;
    height: 3px;
    background: #c9a227;
    margin-bottom: 24px;
  }
  .cover-title {
    font-size: 32pt;
    font-weight: 700;
    color: #8a6e16;
    margin: 0 0 12px;
    line-height: 1.1;
    letter-spacing: -0.5px;
  }
  .cover-subtitle {
    font-size: 13pt;
    color: #B0A898;
    margin: 0 0 50px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .cover-client {
    font-size: 18pt;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px;
  }
  .cover-meta {
    font-size: 10pt;
    color: #B0A898;
    margin: 0;
    font-style: italic;
  }

  /* ─── Contenu principal ─── */
  .content {
    text-align: justify;
  }

  h1 {
    font-size: 17pt;
    font-weight: 700;
    color: #8a6e16;
    margin: 1.4em 0 0.5em;
    padding-bottom: 0.25em;
    border-bottom: 1.5px solid #c9a227;
    page-break-after: avoid;
  }
  h2 {
    font-size: 13pt;
    font-weight: 700;
    color: #b8921f;
    margin: 1.1em 0 0.4em;
    page-break-after: avoid;
  }
  h3 {
    font-size: 11pt;
    font-weight: 700;
    color: #3D3830;
    margin: 0.9em 0 0.3em;
    page-break-after: avoid;
  }
  p {
    margin: 0 0 0.6em;
    orphans: 3;
    widows: 3;
  }
  blockquote {
    margin: 0.7em 0;
    padding: 0.6em 1em;
    border-left: 4px solid #c9a227;
    background: #f8f6f0;
    color: #3D3830;
    font-style: italic;
    page-break-inside: avoid;
  }
  ul, ol {
    margin: 0.5em 0 0.7em 1.2em;
    padding: 0;
  }
  li {
    margin-bottom: 0.2em;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.8em 0;
    font-size: 9.5pt;
    page-break-inside: avoid;
  }
  th, td {
    border: 0.5px solid #e0e0e0;
    padding: 5px 8px;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: #f5e6b3;
    color: #1a1a1a;
    font-weight: 700;
  }
  strong, b { font-weight: 700; color: #1a1a1a; }
  em, i { font-style: italic; }

  /* ─── Toolbar (cachée à l'impression) ─── */
  .toolbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #1a1a1a;
    color: #fff;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    z-index: 999;
  }
  .toolbar-text {
    font-size: 13px;
    color: #f5e6b3;
  }
  .toolbar button {
    background: #c9a227;
    color: #1a1a1a;
    border: 0;
    padding: 8px 18px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    margin-left: 8px;
  }
  .toolbar button:hover {
    background: #e0b83a;
  }
  .toolbar button.secondary {
    background: transparent;
    border: 1px solid #B0A898;
    color: #B0A898;
  }
  .toolbar button.secondary:hover {
    background: rgba(255,255,255,0.05);
    color: #fff;
  }

  .container {
    max-width: 180mm;
    margin: 60px auto 0;
    padding: 20px;
    background: #fff;
    box-shadow: 0 0 15px rgba(0,0,0,0.05);
  }

  @media print {
    body { background: #fff; }
    .toolbar { display: none !important; }
    .container { max-width: none; margin: 0; padding: 0; box-shadow: none; }
    .cover { padding: 0; }
  }
`;

/** Échappe pour insérer du texte dans du HTML attribut/contenu. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function downloadAnalysePdf(opts: GeneratePdfOptions): Promise<void> {
  const sanitizedHtml = DOMPurify.sanitize(opts.htmlContent);

  const fullHtml = `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(opts.filename)}</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
<style>${PRINT_CSS}</style>
</head>
<body>
  <div class="toolbar">
    <span class="toolbar-text">
      Aperçu PDF — ${escapeHtml(opts.clientName)}
    </span>
    <span>
      <button class="secondary" id="btn-close">Fermer</button>
      <button id="btn-print">Imprimer / Enregistrer en PDF</button>
    </span>
  </div>
  <div class="container">
    <section class="cover">
      <div class="cover-decor"></div>
      <h1 class="cover-title">${escapeHtml(opts.title)}</h1>
      ${opts.subtitle ? `<p class="cover-subtitle">${escapeHtml(opts.subtitle)}</p>` : ''}
      <p class="cover-client">${escapeHtml(opts.clientName)}</p>
      <p class="cover-meta">${escapeHtml(opts.date)}</p>
    </section>
    <section class="content">
      ${sanitizedHtml}
    </section>
  </div>
</body>
</html>`;

  // Ouvrir une fenêtre vide puis y écrire le HTML directement.
  // (window.print() ne marche pas de manière fiable depuis une URL blob:.)
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Impossible d\'ouvrir la fenêtre. Vérifie que les pop-ups ne sont pas bloqués.');
  }
  win.document.open();
  win.document.write(fullHtml);
  win.document.close();

  // Attacher les handlers une fois le document écrit (pas d'inline JS pour
  // éviter les problèmes de CSP éventuels du parent).
  win.addEventListener('load', () => {
    const btnPrint = win.document.getElementById('btn-print');
    const btnClose = win.document.getElementById('btn-close');
    if (btnPrint) btnPrint.addEventListener('click', () => win.print());
    if (btnClose) btnClose.addEventListener('click', () => win.close());
    // Lance le dialogue d'impression au chargement (avec un petit délai
    // pour que les fonts Google se chargent).
    setTimeout(() => win.print(), 600);
  });
}
